import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ORDERS } from '@/lib/graphql/queries/customer';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { CheckCircle, ChevronRight, Clock, Package, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { fileUrl } from '../lib/utils';

type OrderStatus = 'PENDING' | 'PROCESSING' | 'DELIVERED' | 'CANCELLED';

interface OrderItem {
  id: string;
  quantity: number;
  productId: string;
  product: {
    id: string;
    name: string;
    thumbnail: string;
    price: number;
    discountPrice: number;
  };
}

interface Order {
  id: string;
  orderNumber: string;
  total: number;
  status: OrderStatus;
  paymentStatus: string;
  createdAt: string;
  items: OrderItem[];
}

const statusConfig = {
  PENDING: { label: 'Хүлээгдэж буй', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
  PROCESSING: { label: 'Боловсруулж буй', icon: Package, color: 'bg-blue-100 text-blue-800' },
  DELIVERED: { label: 'Хүргэгдсэн', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
  CANCELLED: { label: 'Цуцлагдсан', icon: XCircle, color: 'bg-red-100 text-red-800' },
};

const formatDate = (dateString: string) => dayjs(dateString).format('YYYY-MM-DD');
const formatPrice = (price: number) => new Intl.NumberFormat('mn-MN').format(price) + '₮';

const OrderCard = ({ order }: { order: Order }) => {
  const config = statusConfig[order.status] || statusConfig.PENDING;
  const StatusIcon = config.icon;
  const firstItem = order.items[0];
  const extraCount = order.items.length - 1;

  return (
    <Link to={`/orders/${order.id}`}>
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-3 md:p-4">
          <div className="flex items-center gap-3">
            {/* Thumbnail */}
            <div className="flex-shrink-0">
              {firstItem ? (
                <img
                  src={fileUrl(firstItem.product.thumbnail)}
                  alt={firstItem.product.name}
                  className="h-16 w-16 md:h-20 md:w-20 object-cover rounded-lg border border-border"
                />
              ) : (
                <div className="h-16 w-16 md:h-20 md:w-20 bg-muted rounded-lg flex items-center justify-center">
                  <Package className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-semibold text-sm md:text-base">#{order.orderNumber}</span>
                <Badge className={`${config.color} text-[10px] md:text-xs px-1.5 py-0.5`}>
                  <StatusIcon className="h-3 w-3 mr-0.5" />
                  {config.label}
                </Badge>
              </div>
              <p className="text-xs md:text-sm text-muted-foreground">{formatDate(order.createdAt)}</p>
              <p className="text-xs text-muted-foreground">
                {order.items.length} бүтээгдэхүүн
                {extraCount > 0 && ` (+${extraCount})`}
              </p>
            </div>

            {/* Price & Arrow */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="font-bold text-sm md:text-base">{formatPrice(order.total)}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const OrderSkeleton = () => (
  <Card>
    <CardContent className="p-3 md:p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-16 w-16 md:h-20 md:w-20 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-5 w-20" />
      </div>
    </CardContent>
  </Card>
);

const Orders = () => {
  const [activeTab, setActiveTab] = useState<string>('all');

  const { data, loading } = useQuery(ORDERS, {
    variables: {
      status: activeTab === 'all' ? undefined : activeTab,
      page: 1,
      limit: 20,
    },
    fetchPolicy: 'cache-and-network',
  });

  const orders: Order[] = data?.customerOrders || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 md:gap-3 mb-6 md:mb-8">
            <Package className="h-6 w-6 md:h-8 md:w-8 text-primary" />
            <h1 className="text-xl md:text-3xl font-bold">Миний захиалгууд</h1>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-4 mb-4 md:mb-6 h-auto">
              <TabsTrigger value="all" className="text-xs md:text-sm py-2">Бүгд</TabsTrigger>
              <TabsTrigger value="processing" className="text-xs md:text-sm py-2">Хүлээгдэж</TabsTrigger>
              <TabsTrigger value="delivered" className="text-xs md:text-sm py-2">Хүргэгдсэн</TabsTrigger>
              <TabsTrigger value="cancelled" className="text-xs md:text-sm py-2">Цуцлагдсан</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="space-y-3 md:space-y-4">
              {loading ? (
                <>
                  <OrderSkeleton />
                  <OrderSkeleton />
                  <OrderSkeleton />
                </>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 md:h-16 md:w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-base md:text-lg font-medium mb-2">Захиалга олдсонгүй</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Та одоогоор захиалга хийгээгүй байна
                  </p>
                  <Link to="/shop" className="text-primary hover:underline text-sm">
                    Дэлгүүр үзэх
                  </Link>
                </div>
              ) : (
                orders.map((order) => <OrderCard key={order.id} order={order} />)
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Orders;
